import AddIcon from '@mui/icons-material/Add';
import { Box, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useSelector } from "react-redux";
import WarehouseProductCard from "../../../../../components/global/cards/products/warehouse/WarehouseProductCard";
import ShDataGrid2 from "../../../../../components/theme/datagrid2/ShDataGrid2";
import ShLabel from "../../../../../components/theme/label/ShLabel";
import SidepanelLoader from '../../../../../components/theme/sidepanel2/SidepanelLoader';
import ShTextSkeleton from "../../../../../components/theme/skeleton/ShTextSkeleton";
import Text from "../../../../../components/theme/text/Text";
import { useGetReplenishListsQuery } from "../../../../../newapi/replenish/replenishListSlice";
import { tokens } from "../../../../../theme";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from '../../../../../utils/permissions';
import ShLabelSkeleton from '../../../../../components/theme/skeleton/ShLabelSkeleton';
import { getReplenishStateData } from '../../../../../utils/labelColorText';
import { REPLENISHLIST_FILTER_KEYS } from '../../../../../components/global/DataGrid/Filter';

const SpReplenishListDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpReplenishListDetails/SpReplenishListDetails"));

const GridActions = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();

    return <></>;
}

let gridOptions = {
    export:true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    filter: [REPLENISHLIST_FILTER_KEYS.STATE, REPLENISHLIST_FILTER_KEYS.PRODUCT, REPLENISHLIST_FILTER_KEYS.WAREHOUSE],
};


const ProductOverview = (props) => {
    const { t,i18n } = useTranslation();

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [config, setConfig] = useState({});
    const [isExtended, setIsExtended] = useState(false);
    const [spReplenishListLoading, setSpReplenishListLoading] = useState();

    const hasCreatePermission = useSelector((state) => 
        selectUserPermission(state, [UserPrivileges.PRODUCT_MANAGE])
    );

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
            mobileActions: <GridActions hasCreatePermission={hasCreatePermission}></GridActions>,
        });

        return () => {
            
        }
    },[]);
    
    const [replenishListId, setReplenishListId] = useState();
    const replenishListIdRef = useRef(replenishListId);
    
    const getReplenishListDetails = (params) => {
        if (!params) return;
    
        const newListId = parseInt(params.id);
        // Only update state if the productId has actually changed
        if (replenishListIdRef.current !== newListId) {
            if(!spReplenishListLoading){
                setSpReplenishListLoading(true);
            }
            setReplenishListId(newListId);
            replenishListIdRef.current = newListId; // Update the ref to the new value
        }
            setIsExtended(true);
    };

    const columns = [
        {
            field: "createdAt",
            headerName: t("created_at"),
            flex:2,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton palette={theme.palette.mode === "dark" ? "pastel" : "normal"} variant={theme.palette.mode === "dark" ? "blue" : "primary"} />
                }

                let date = new Date(params.row.createdAt);

                return (
                    <Text>
                        {date.toLocaleString(i18n.language)}
                    </Text>
                );
            }
        },
        {
            field: "userName",
            headerName: t("user_name"),
            flex:2,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                return params.row.userName;
            },
        },
        {
            field: "itemCount",
            headerName: t("amount_of_locations"),
            sortable:false,
            flex:1,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                return params.row.itemCount;
            },
        },
        {
            field: "lineCount",
            headerName: t("amount_of_replenishlines"),
            sortable:false,
            flex:1,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                return params.row.lineCount;
            },
        },
        {
            field: "totalProducts",
            headerName: t("total_amount_of_products"),
            sortable:false,
            flex:1,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                return params.row.lineCount;
            },
        },
        {
            field: "warehouse",
            headerName: t("warehouse"),
            sortable:false,
            flex:1,
            filter: REPLENISHLIST_FILTER_KEYS.WAREHOUSE,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                return params.row.warehouseName ?? "";
            },
        },
        {
            field: "state", 
            headerName: t("status"),
            flex:1,
            filter: REPLENISHLIST_FILTER_KEYS.STATE,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShLabelSkeleton/>
                }
                
                const { labelcolor, labeltext } = getReplenishStateData(params.row.state, theme);
              
                return (
                    <ShLabel size={32} palette="normal" variant={labelcolor}>
                      {t(labeltext)}
                    </ShLabel>
                  );
              }
        },
        {
            field: "phoneView", 
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (cellValues) => {
                // console.log(cellValues.row);
                return (
                    <WarehouseProductCard onClick={() => getReplenishListDetails(cellValues)} skeleton={cellValues.row.skeleton ?? false} {...cellValues.row}/>
                    // <Box
                    //     sx={{
                    //         background:`${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                    //         paddingX:2,
                    //         paddingY:1.5,
                    //         borderRadius:2
                    //     }}
                    // >

                    // </Box>
                )
            }
        }
    ];

    return (
        <Box height={1} display={"flex"} flexDirection={"column"}>
            <ShDataGrid2 key={theme.palette.mode}
                content={useGetReplenishListsQuery}
                config={config}
                gridActions={<GridActions  hasCreatePermission={hasCreatePermission}></GridActions>} 
                onRowClick={getReplenishListDetails}
                title={t('overview')} 
                gridOptions={gridOptions} 
                sortModel={{field: columns[0].field,type:"desc"}}
                columns={columns}>
            </ShDataGrid2>
            {spReplenishListLoading && 
                <SpReplenishListDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    replenishListId={replenishListId}
                />
            }
        </Box>
    );
}
export default ProductOverview;